import { useState, useEffect } from 'react';
import { Modal, Button, ListGroup, Badge, Form, Spinner, Alert } from 'react-bootstrap';
import ModalHelp from '../../../components/ModalHelp';
import { toast } from 'react-toastify';
import ContaServicoViewModel from '../../../viewmodel/ContaServicoViewModel';

interface PermissoesCustomizadasModalProps {
    show: boolean;
    conta: any;
    onClose: () => void;
    onRefresh: () => void;
}

export default function PermissoesCustomizadasModal({ show, conta, onClose, onRefresh }: PermissoesCustomizadasModalProps) {
    const [perfilNome, setPerfilNome] = useState<string>('');
    const [permissoesPerfil, setPermissoesPerfil] = useState<any[]>([]);
    const [permissoesCustomizadas, setPermissoesCustomizadas] = useState<any[]>([]);
    const [permissoesDisponiveis, setPermissoesDisponiveis] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [showAdicionar, setShowAdicionar] = useState(false);
    const [permissaoSelecionada, setPermissaoSelecionada] = useState('');
    const [justificativa, setJustificativa] = useState('');

    useEffect(() => {
        if (show && conta) {
            carregarPermissoes();
            carregarPermissoesDisponiveis();
        }
    }, [show, conta]);

    const carregarPermissoes = async () => {
        setLoading(true);
        try {
            const data = await ContaServicoViewModel.getPermissoesCustomizadas(conta.clientId);
            setPerfilNome(data.perfilNome || 'N/A');
            setPermissoesPerfil(data.permissoesPerfil || []);
            setPermissoesCustomizadas(data.permissoesCustomizadas || []);
        } catch (error) {
            toast.error('Erro ao carregar permissões');
        } finally {
            setLoading(false);
        }
    };

    const carregarPermissoesDisponiveis = async () => {
        try {
            const data = await ContaServicoViewModel.getPermissoesDisponiveis();
            setPermissoesDisponiveis(data);
        } catch (error) {
            console.error('Erro ao carregar permissões disponíveis');
        }
    };

    const handleAdicionar = async () => {
        if (!permissaoSelecionada || !justificativa.trim()) {
            toast.warning('Selecione uma permissão e forneça uma justificativa');
            return;
        }

        try {
            await ContaServicoViewModel.adicionarPermissaoCustomizada(conta.clientId, permissaoSelecionada, justificativa);
            toast.success('Permissão adicionada com sucesso');
            setShowAdicionar(false);
            setPermissaoSelecionada('');
            setJustificativa('');
            await carregarPermissoes();
            onRefresh();
        } catch (error: any) {
            toast.error(error.message || 'Erro ao adicionar permissão');
        }
    };

    const handleRemover = async (permissaoId: string) => {
        if (!window.confirm('Deseja realmente remover esta permissão customizada?')) return;

        try {
            await ContaServicoViewModel.removerPermissaoCustomizada(conta.clientId, permissaoId);
            toast.success('Permissão removida com sucesso');
            await carregarPermissoes();
            onRefresh();
        } catch (error: any) {
            toast.error(error.message || 'Erro ao remover permissão');
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Permissões da Conta de Serviço</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <ModalHelp text="Gerencie permissões extras além das do perfil base. Adicione permissões customizadas com justificativa ou remova permissões existentes." />
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <>
                        <h5>Perfil: <Badge bg="secondary">{perfilNome}</Badge></h5>
                        <hr />
                        
                        <h6 className="mt-3">Permissões do Perfil</h6>
                        {permissoesPerfil.length === 0 ? (
                            <p className="text-muted">Nenhuma permissão do perfil</p>
                        ) : (
                            <ListGroup className="mb-3">
                                {permissoesPerfil.map((perm) => (
                                    <ListGroup.Item key={perm.id}>
                                        <Badge bg="info" className="me-2">PERFIL</Badge>
                                        <strong>{perm.nome}</strong>
                                        <p className="mb-0 small text-muted">{perm.descricao}</p>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        )}

                        <h6 className="mt-4">Permissões Customizadas</h6>
                        <Alert variant="info" className="small">
                            Permissões customizadas são adicionadas além das permissões do perfil.
                        </Alert>
                        
                        {permissoesCustomizadas.length === 0 ? (
                            <p className="text-muted">Nenhuma permissão customizada adicionada</p>
                        ) : (
                            <ListGroup className="mb-3">
                                {permissoesCustomizadas.map((perm, index) => (
                                    <ListGroup.Item key={`${perm.id}-${index}`}>
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <Badge bg="warning" text="dark" className="me-2">CUSTOMIZADA</Badge>
                                                <strong>{perm.nome}</strong>
                                                <p className="mb-0 small text-muted">{perm.descricao}</p>
                                            </div>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleRemover(perm.id)}
                                                title="Remover permissão customizada"
                                            >
                                                ×
                                            </Button>
                                        </div>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        )}

                        {!showAdicionar ? (
                            <Button variant="primary" onClick={() => setShowAdicionar(true)}>
                                + Adicionar Permissão
                            </Button>
                        ) : (
                            <div className="border rounded p-3 bg-light">
                                <Form.Group className="mb-3">
                                    <Form.Label>Permissão</Form.Label>
                                    <Form.Select
                                        value={permissaoSelecionada}
                                        onChange={(e) => setPermissaoSelecionada(e.target.value)}
                                    >
                                        <option value="">Selecione uma permissão</option>
                                        {permissoesDisponiveis.map((perm) => (
                                            <option key={perm.id} value={perm.id}>
                                                {perm.nome} - {perm.descricao}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Justificativa *</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={justificativa}
                                        onChange={(e) => setJustificativa(e.target.value)}
                                        placeholder="Explique por que esta permissão é necessária..."
                                    />
                                </Form.Group>

                                <div className="d-flex gap-2">
                                    <Button variant="success" onClick={handleAdicionar}>
                                        Confirmar
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setShowAdicionar(false);
                                            setPermissaoSelecionada('');
                                            setJustificativa('');
                                        }}
                                    >
                                        Cancelar
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
}
